import { createSlice } from "@reduxjs/toolkit";
import { FORM_KEYS } from "../pages/self-service/constants";
import { cloneDeep } from "lodash";
import { getConventionsSelector } from "./conventionsSlice";
import { deleteProduct } from "./conventionsSlice";
import { uploadBackgroundImage, uploadProductImage } from "./webdavSlice";
import { IMAGES } from "../utils/constants";

// CONSTANTS ------------------------------------------------------------------

const FORM_INITIAL_STATE = {
  [FORM_KEYS.ANNOUNCEMENT]: "",
  [FORM_KEYS.BACKGROUND_COLOR]: "",
  [FORM_KEYS.CNAME]: "",
  [FORM_KEYS.DARK_MODE]: false,
  [FORM_KEYS.IS_PUBLIC_FUNKO]: false,
  [FORM_KEYS.IS_PUBLIC_LOUNGEFLY]: false,
  [FORM_KEYS.PRODUCTS]: {},
  [FORM_KEYS.TITLE]: "",
  [FORM_KEYS.ADD_PRODUCT_SKU]: "",
};

// SLICE ----------------------------------------------------------------------

/**
 * @desc Self Service Slice.
 */
export const selfServiceSlice = createSlice({
  name: "selfService",
  initialState: {
    pending: false,
    form: FORM_INITIAL_STATE,
  },
  reducers: {
    resetForm: (state) => {
      state.form = FORM_INITIAL_STATE;
    },
    resetProductForm: (state) => {
      state.form[FORM_KEYS.PRODUCTS] = {};
    },
    updateForm: (state, action) => {
      if (
        FORM_KEYS.CNAME in action.payload &&
        action.payload[FORM_KEYS.CNAME] !== state.form[FORM_KEYS.CNAME]
      ) {
        state.form[FORM_KEYS.PRODUCTS] = {};
      }
      state.form = {
        ...(state.form || {}),
        ...(action.payload || {}),
      };
    },
    updateProductForm: (state, action) => {
      const { formProduct, sku } = action.payload;
      state.form[FORM_KEYS.PRODUCTS][sku] = {
        ...(cloneDeep(state.form[FORM_KEYS.PRODUCTS][sku]) || {}),
        ...formProduct,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // Delete Product
      .addCase(deleteProduct.pending, (state) => {
        state.pending = true;
      })
      .addCase(deleteProduct.fulfilled, (state, action) => {
        const sku = action.meta.arg.sku;
        const newProducts = cloneDeep(state.form[FORM_KEYS.PRODUCTS]);
        if (sku && newProducts) {
          if (sku in newProducts) {
            delete newProducts[sku];
          }
          state.form[FORM_KEYS.PRODUCTS] = newProducts;
        }
      })
      .addCase(deleteProduct.rejected, (state) => {
        state.pending = false;
      })

      // Upload Background Image
      .addCase(uploadBackgroundImage.pending, (state) => {
        state.pending = true;
      })
      .addCase(uploadBackgroundImage.fulfilled, (state, action) => {
        state.form[FORM_KEYS.BACKGROUND_IMAGE] = action.payload.url;
        state.pending = false;
      })
      .addCase(uploadBackgroundImage.rejected, (state) => {
        state.pending = false;
      })

      // Upload Product Image
      .addCase(uploadProductImage.pending, (state) => {
        state.pending = true;
      })
      .addCase(uploadProductImage.fulfilled, (state, action) => {
        const { sku } = action.meta.arg;
        const image = action.payload.url;
        if (sku && image) {
          state.form[FORM_KEYS.PRODUCTS][sku][FORM_KEYS.PRODUCT_IMAGE] = image;
        }
        state.pending = false;
      })
      .addCase(uploadProductImage.rejected, (state) => {
        state.pending = false;
      });
  },
});

// ACTIONS --------------------------------------------------------------------

export const { updateForm } = selfServiceSlice.actions;

// SELECTORS ------------------------------------------------------------------

export const getSelfServiceSelector = (state) => state.selfService;

export const getSelfServiceFormSelector = (state) => state.selfService.form;

export const getSelfServiceCnameSelector = (state) =>
  getSelfServiceFormSelector(state)[FORM_KEYS.CNAME];

export const getSelfServiceDarkModeSelector = (state) =>
  getSelfServiceFormSelector(state)[FORM_KEYS.DARK_MODE];

export const getSelfServiceBackgroundImageSelector = (state) =>
  getSelfServiceFormSelector(state)[FORM_KEYS.BACKGROUND_IMAGE];

export const getSelfServiceProductsSelector = (state) =>
  getSelfServiceFormSelector(state)[FORM_KEYS.PRODUCTS];

export const getSelfServiceProductImageSelector = (state) => (sku) => {
  const products = getSelfServiceProductsSelector(state);
  if (products && sku in products) {
    const product = products[sku];

    if (product) {
      return product[FORM_KEYS.PRODUCT_IMAGE] || IMAGES.PRODUCT.DEFAULT;
    }
  }
  return IMAGES.PRODUCT.DEFAULT;
};

export const getSelfServiceAddSKUSelector = (state) =>
  getSelfServiceFormSelector(state)[FORM_KEYS.ADD_PRODUCT_SKU];

export const getSelfServiceConvention = (state) => {
  const cname = getSelfServiceCnameSelector(state);
  const conventions = getConventionsSelector(state);

  if (!cname || !conventions) return;

  return conventions.find((convention) => convention.name === cname);
};

// REDUCER --------------------------------------------------------------------

export default selfServiceSlice.reducer;
